import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'strasidelna-mista-ceska',
    title: 'Brána do pekel, kostel duchů a useknutá ruka: 7 nejstrašidelnějších míst Česka',
    metaTitle: '7 nejstrašidelnějších míst Česka',
    perex:
      'Halloween letos padá na sobotu a Dušičky na pondělí. Jestli chcete strávit prodloužený podzimní víkend trochu jinak než u dýně, máme pro vás sedm míst, ze kterých běhá mráz po zádech. S legendou, pravdou i tipem, kde se potom v klidu vyspat.',
    coverImage: 'https://images.unsplash.com/photo-1642137837405-8a80225314b1?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Zřícenina hradu zahalená v husté mlze',
    publishedAt: '2026-10-22',
    author: 'Redakce Mátevolno',
    tags: ['Halloween', 'Dušičky', 'podzim', 'zajímavosti', 'výlety'],
  },
  content: `Konec října je v Česku jako stvořený pro strašení. Tma přichází po páté, ráno leží v údolích mlha a 31. října letos připadá na sobotu. Den předtím končí podzimní prázdniny, v pondělí jsou Dušičky. Ideální chvíle vyrazit za místy, o kterých se vypráví potichu.

U každého místa najdete legendu, kus pravdy, naši stupnici strašidelnosti a tip, kde se po výletě schovat pod peřinu.

## 1. Hrad Houska: brána do pekel

**Kraj:** Kokořínsko | **Strašidelnost:** 5/5

Gotický hrad uprostřed lesů na Kokořínsku nedává smysl. Podle [Kudy z nudy](https://www.kudyznudy.cz/aktivity/hrad-houska) nestál u hranic ani u obchodní stezky a nebylo v okolí nic, co by musel chránit. Neví se ani jistě, kdo ho postavil. Pověst to vysvětluje po svém: hrad nebyl postaven, aby chránil něco venku, ale aby zadržel něco uvnitř. Pod ním má být průrva do pekla, kterou hlídá černý mnich bez tváře. Jedni ji hledají v kapli, druzí ve studni.

**Pravda:** Hradem skutečně prostupuje obří skála, kterou uvidíte zvenku i zevnitř. Nádvoří má pozoruhodnou akustiku a v bývalé kočárovně visí jedenáct obrazů s pekelnými tresty. O Housce psaly National Geographic, Atlas Obscura i The Guardian.

**Kde přespat:** mezi pískovcovými skalami [Českého ráje a Kokořínska](/pobyty/cesky-raj-a-kokorinsko).

## 2. Kostnice v Sedlci: lustr ze všech kostí lidského těla

**Kraj:** Kutná Hora | **Strašidelnost:** 4/5

Uprostřed malé kaple visí lustr, ve kterém je podle tradice každá kost, kterou člověk má. Podle [Novinek](https://www.novinky.cz/clanek/cestovani-v-kutne-hore-maji-z-kosti-lustr-i-erb-40285786) je v kostnici kolem šedesáti tisíc kostí a lebek z obětí moru ve čtrnáctém století a z husitských válek.

**Pravda:** Hřbitov byl vyhlášený kvůli hlíně, kterou sem prý kdysi přivezli z Jeruzaléma. Mělo se za to, že v ní tělo zetleje za čtyřiadvacet hodin, a tak se tu chtěli nechat pohřbít lidé z celé střední Evropy. Dnešní výzdobu vytvořil v roce 1870 řezbář František Rint. Z kostí poskládal i erb Schwarzenberků, na kterém havran klove Turkovi oko. Kostnici ročně navštíví přes 400 tisíc lidí, tak jeďte brzy ráno.

**Kde přespat:** kousek od Kutné Hory ve [středních Čechách](/pobyty/stredni-cechy).

## 3. Kostel v Lukové: 32 duchů v lavicích

**Kraj:** Plzeňsko | **Strašidelnost:** 5/5

V roce 1968 se během pohřbu zřítila část stropu kostela svatého Jiří. Místní to vzali jako znamení, kostel opustili a nechali ho chátrat. Víc než o čtyřicet let později, v roce 2012, do něj student Jakub Hadrava umístil jako svou bakalářskou práci 32 sádrových postav v bílých pláštích. Sedí v lavicích, stojí v uličce a mají zahalené tváře. Představují původní německé obyvatele, kteří museli po válce odejít.

**Pravda:** Duchové kostel zachránili. Podle [Wikipedie](https://cs.wikipedia.org/wiki/Kostel_svat%C3%A9ho_Ji%C5%99%C3%AD_%28Lukov%C3%A1%29) přilákala instalace návštěvníky z celého světa a ze vstupného se opravila střecha, krov, okna i fasáda. Kostel bývá otevřený v sobotu odpoledne od dubna do října. Halloween letos padá na poslední říjnovou sobotu, takže to může být poslední šance sezóny. Otevírací dobu si před cestou ověřte.

**Kde přespat:** v [západních Čechách](/pobyty/zapadni-cechy). Luková leží v Manětínské oblasti tmavé oblohy, takže večer se vyplatí podívat se i nahoru.

## 4. Čertovo jezero: kde se utopil čert

**Kraj:** Šumava | **Strašidelnost:** 3/5

Ledovcové jezero pod Jezerní horou je hluboké až 36 metrů a obklopené strmými, tmavými stěnami. Podle pověsti ho vyhloubil sám čert. Chtěl do pekla odnést dívku, která mu ale lstí přivázala k ocasu kámen, a čert se s ním zřítil do hlubiny. Z vody prý dodnes vylézají skřeti a podivné postavy a všechna šumavská jezera jsou pod zemí propojená.

**Pravda:** Podle [Kudy z nudy](https://www.kudyznudy.cz/aktivity/certovo-jezero) je Čertovo jezero jediné šumavské jezero, jehož voda teče do Dunaje. Sousední Černé jezero odtéká do Severního moře. A strmé břehy obou jezer jsou jediná místa na celé Šumavě, kde vznikají laviny. Cesta k němu vede od Černého jezera po kamenité stezce, takže pevné boty jsou nutnost.

**Kde přespat:** na [Šumavě](/pobyty/sumava), ideálně v okolí Železné Rudy.

## 5. Kostnice u svatého Jakuba: druhá největší v Evropě

**Kraj:** Brno | **Strašidelnost:** 4/5

Pod dlažbou Jakubského náměstí v centru Brna chodí denně tisíce lidí, aniž by tušily, co mají pod nohama. Podle [Wikipedie](https://cs.wikipedia.org/wiki/Kostnice_u_sv._Jakuba) tu leží pozůstatky až padesáti tisíc lidí. Větší kostnici v Evropě mají jen pařížské katakomby.

**Pravda:** Kostnice byla od konce osmnáctého století zapečetěná a zapomenutá. Znovu ji objevili až archeologové v roce 2001 při průzkumu náměstí a veřejnosti se otevřela v roce 2012. Pro výlet z Brna je to ideální program na deštivé dopoledne.

**Kde přespat:** na [jižní a střední Moravě](/pobyty/jizni-a-stredni-morava), kde vás po kostnici spraví sklenka vína.

## 6. Useknutá ruka u svatého Jakuba v Praze

**Kraj:** Praha | **Strašidelnost:** 3/5

Když vejdete do baziliky svatého Jakuba na Starém Městě, podívejte se vpravo nad vchod. Na řetězu tam visí zčernalá lidská ruka. Podle [Pražského deníku](https://prazsky.denik.cz/zpravy_region/v-nenapadnem-kostele-visi-useknuta-ruka-jakemu-nestastnikovi-patri-20180930.html) se kolem roku 1400 v noci vloupal do kostela zloděj a sáhl po perlovém náhrdelníku na soše Panny Marie. Socha ho chytila za zápěstí a nepustila. Ráno ho nedokázal nikdo vyprostit, a tak zavolali kata, který ruku usekl. Visí tam dodnes jako výstraha.

**Pravda:** Legenda je legenda, ale ruka je skutečná a přežila dokonce velký požár kostela v roce 1689.

**Kde přespat:** ne v Praze. Kousek za ní ve [středních Čechách](/inspirace/chalupa-do-hodiny-z-prahy).

## 7. Bílá paní: když nosí černé rukavice

**Kraj:** jižní Čechy | **Strašidelnost:** 2/5

Nejslavnější české strašidlo je vlastně hodné. Bílá paní se zjevuje na bývalých rožmberských sídlech, v Českém Krumlově, Jindřichově Hradci, Třeboni nebo Telči. Podle [Wikipedie](https://cs.wikipedia.org/wiki/B%C3%ADl%C3%A1_pan%C3%AD) ohlašuje osud rodu. Když má bílé rukavice a klíče u pasu, čeká rod něco dobrého. Když černé, blíží se neštěstí nebo smrt.

**Pravda:** S Bílou paní se obvykle ztotožňuje Perchta z Rožmberka, historicky je to ale sporné. V Jindřichově Hradci se k ní váže tradice sladké kaše, kterou na Zelený čtvrtek dostávali chudí. Založila ji ve čtrnáctém století Markéta z Hardeggu a vydržela až do osmnáctého století.

**Kde přespat:** v [jižních Čechách](/pobyty/jizni-cechy), třeba v roubence uprostřed rybníků, kde se v mlze ztrácí hráze.

![Mlhavý les s úzkou pěšinou](https://images.unsplash.com/photo-1544039161-b0c20826c6f6?w=1200&q=80&auto=format&fit=crop)

## Po strašení k ohni

Nejlepší část strašidelného výletu přichází až potom. Vrátit se za tmy na chalupu, zatopit, zavřít okenice a vyprávět si, co kdo viděl. Na to se hodí [chalupa s krbem](/pobyty/s-krbem) na samotě, kde je kolem opravdová tma.

Jestli chcete spojit strašení s prodlouženým víkendem, tipy najdete v článku [Prodloužený víkend na chalupě](/inspirace/prodlouzeny-vikend-na-chalupe).

::nabidky{facility="indoorFireplace" text="Prohlédnout chalupy s krbem"}

::cta{text="Najít volný termín na dušičkový víkend"}`,
}
